/**
 * VP-302 — jedna reguła liczb w drill-downzie dashboardu (kampania → zestaw → reklama).
 *
 * Powód wydzielenia: wiersz zestawu i wiersz reklamy liczyły LEADY własnym `?:` w JSX-ie, a nagłówek
 * kampanii — nadpisaniem z Ads Managera po stronie API (VP-232). Reguły się rozjechały: kampania
 * pokazywała 95 (Ads Manager), zestawy sumowały się do 61 (rekordy), a zestaw bez zaimportowanych
 * leadów rysował „—". Tu leży JEDNA kolejność źródeł, wspólna dla obu poziomów, i wyliczenie reszty,
 * której nie da się przypisać do żadnego dziecka.
 *
 * `packages/shared`, a nie `apps/web`, bo to reguła liczenia — chcemy ją mieć pod testem (vitest
 * czyta `server-tests/`, które importuje `@shared/*`), a nie tylko pod Playwrightem.
 */

export type ObjectLeadSources = {
  /** „Results = Leads (Form)" z Ads Managera dla TEGO obiektu. >0 wygrywa ze wszystkim. */
  adsManagerLeads: number;
  /** Leady policzone z naszych rekordów (`tracking_conversions`) — tor metricsV2. */
  recordLeads: number | undefined;
  /** Leady z CRM (stary tor atrybucji UTM). */
  crmLeads: number | undefined;
  /** Liczba z platformy reklamowej — ostatnia deska ratunku starego toru. */
  platformLeads: number;
  metricsV2On: boolean;
};

/**
 * LEADY jednego obiektu Meta. Kolejność źródeł jest TA SAMA co dla nagłówka kampanii:
 *
 *  1. Ads Manager — kampanie z formularzem błyskawicznym czytamy 1:1 z Meta (ustalenie VP-232).
 *     Bez tego suma zestawów nie mogła się zgadzać z kampanią, bo import zawsze ma mniej leadów
 *     (odrzuca zgłoszenia bez e-maila i telefonu, nie widzi korekt po stronie Meta).
 *  2. Rekordy — gdy metricsV2 ON. Brak wpisu w mapie to realne zero, nie brak danych.
 *  3. Stary tor: CRM, a gdy i tego nie ma — liczba z platformy.
 */
export function resolveObjectLeads(sources: ObjectLeadSources): number {
  if (sources.adsManagerLeads > 0) return sources.adsManagerLeads;
  if (sources.metricsV2On) return sources.recordLeads ?? 0;
  return sources.crmLeads ?? sources.platformLeads;
}

/**
 * Ile zdarzeń rodzica nie trafiło do żadnego dziecka.
 *
 * Nie zaokrąglamy tego do zera po cichu: dodatnia reszta to jawny wiersz „nieprzypisane" w tabeli
 * (zdarzenia, przy których nie wiemy, do którego zestawu należą — np. rozmowy z pullera GHL bez id
 * obiektu Meta). Ujemna reszta oznacza, że dzieci sumują się PONAD rodzica, czyli double-count —
 * wtedy zwracamy 0 i zostawiamy to do wyłapania w teście niezmiennika, a nie do pokazania userowi.
 */
export function resolveUnattributed(parentValue: number, childrenSum: number): number {
  const rest = parentValue - childrenSum;
  return rest > 0 ? rest : 0;
}
